import React, {
  createContext,
  useContext,
  PropsWithChildren,
  useMemo,
  useState,
  useEffect,
} from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';

import { InputType } from '@src/types/main';

type BaseMessage = {
  messageType: 'connection' | 'results';
};

export type ConnectionMessage = BaseMessage & {
  messageType: 'connection';
  sessionId: string;
};

export type ResultsMessage = BaseMessage & {
  messageType: 'results';
  errorMessage: string | null;
  inputType: InputType;
  source: string;
  text: string | null;
  id: string;
};

type MessageType = ConnectionMessage | ResultsMessage;

type WebSocketContextType = {
  sendMessage: (message: string) => void;
  readyState: number;
  lastMessage: WebSocketEventMap['message'] | null;
  lastJsonMessage: MessageType;
  connectionStatus: string;
  sessionId: string | null;
};

type WebSocketProviderType = PropsWithChildren & {
  url: string;
};

export const isConnectionMessage = (
  message: MessageType
): message is ConnectionMessage => {
  return message.messageType === 'connection';
};

export const isResultsMessage = (
  message: MessageType
): message is ResultsMessage => {
  return message.messageType === 'results';
};

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error(
      'useWebSocketContext must be used within a WebSocketProvider'
    );
  }
  return context;
};

export const WebSocketProvider: React.FC<WebSocketProviderType> = ({
  url,
  children,
}) => {
  const { sendMessage, lastMessage, readyState, lastJsonMessage } =
    useWebSocket<MessageType>(url, {
      shouldReconnect: () => true,
    });

  const [sessionId, setSessionId] = useState<string | null>(null);

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[readyState];

  useEffect(() => {
    if (
      readyState === ReadyState.CLOSED ||
      readyState === ReadyState.CONNECTING
    ) {
      setSessionId(null);
    }

    if (lastJsonMessage) {
      if (isConnectionMessage(lastJsonMessage)) {
        setSessionId(lastJsonMessage.sessionId);
      }
    }
  }, [lastJsonMessage, readyState]);

  const contextValue = useMemo(
    () => ({
      sendMessage,
      readyState,
      lastMessage,
      lastJsonMessage,
      connectionStatus,
      sessionId,
    }),
    [
      sendMessage,
      readyState,
      lastMessage,
      lastJsonMessage,
      connectionStatus,
      sessionId,
    ]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};
